import { HubItemCategoryIdentifierModel, HubTileLayoutKind } from "client/Api/Hub/common";
import {
	Checkbox,
	ComboBoxIntermediateState,
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { SaveHubTileLayoutFormModel } from "./EditHubTile";
import { never } from "client_legacy/ndaw/errors";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { AlertManager, useAlerts } from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HubItemDefaultViewSelector } from "./HubItemDefaultViewSelector";
import { SpotlightItemSelector } from "./SpotlightItemSelector";
import { listCategories } from "client/Api/Workspace/LiveInformation/Hub/hubItemCategoriesLiveInformation";
import { TimeZone } from "client/DateAndTime/timezones";

export type HubItemFormSectionProps = {
	attachable: IValueInputAttachable<SaveHubTileLayoutFormModel>;
	spotlightItemState: ComboBoxIntermediateState<number>;
	isSelectedItemNotAvailable: boolean;
	timezone: TimeZone;
};

export const HubItemFormSection = (props: HubItemFormSectionProps): ReactElement => {
	const {
		attachable,
		spotlightItemState,
		isSelectedItemNotAvailable,
		timezone
	} = props;

	const {
		kind,
		itemKind
	} = attachable.value;

	if (kind === HubTileLayoutKind.Feed) {
		throw new Error("Hub items don't support the Feed layout");
	}

	const [categories, setCategories] = useState<readonly HubItemCategoryIdentifierModel[]>([]);

	const cancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);
	const alertManager: AlertManager = useAlerts();

	const loadCategories = useCallback(
		async (itemKind: HubItemKind): Promise<void> => {
			cancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			cancellationRef.current = cancellation;

			setCategories([]);
			try {
				const data: HubItemCategoryIdentifierModel[] = await listCategories(
					itemKind,
					cancellation.signal
				);

				setCategories(data);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[alertManager]
	);

	useEffect(
		(): (() => void) => {
			loadCategories(itemKind);

			return (): void => {
				cancellationRef.current
					?.abort();
			};
		},
		[itemKind, loadCategories]
	);

	return (
		<>
			<FormItem label={localize("_Layout")}>
				<Select
					{...attachable.for("kind").attachToSelect()}
					options={layoutOptions}
					menuTitle={localize("_Layout")}
				/>

				<ValidationError {...attachable.for("kind").attachToValidationError()} />
			</FormItem>

			{kind === HubTileLayoutKind.Spotlight && (
				<SpotlightItemSelector
					attachable={attachable.for("itemId")}
					spotlightItemState={spotlightItemState}
					kind={itemKind}
					timezone={timezone}
					isSelectedItemNotAvailable={isSelectedItemNotAvailable}
				/>
			)}

			{kind !== HubTileLayoutKind.Spotlight && (
				<HubItemDefaultViewSelector
					attachable={attachable.for("defaultView")}
					categories={categories}
				/>
			)}

			{kind === HubTileLayoutKind.Carousel && (
				<FormItem>
					<Checkbox
						{...attachable.for("isAutoPlayEnabled").attachToCheckbox()}
						text={localize("_EnableAutoPlay")}
					/>

					<ValidationError {...attachable.for("isAutoPlayEnabled").attachToValidationError()} />
				</FormItem>
			)}
		</>
	);
};
HubItemFormSection.displayName = "HubItemFormSection";

const layoutOptions: readonly SelectItem<Exclude<HubTileLayoutKind, HubTileLayoutKind.Feed>>[] = [
	{
		text: localize("_List"),
		value: HubTileLayoutKind.List
	},
	{
		text: localize("_Tiles"),
		value: HubTileLayoutKind.Grid
	},
	{
		text: localize("_Carousel"),
		value: HubTileLayoutKind.Carousel
	},
	{
		text: localize("_Spotlight"),
		value: HubTileLayoutKind.Spotlight
	}
];

type HubItemLayoutKind = Exclude<HubTileLayoutKind, HubTileLayoutKind.Feed>;

export function isHubItemLayout(layout: HubTileLayoutKind): layout is HubItemLayoutKind {
	switch (layout) {
		case HubTileLayoutKind.Carousel:
		case HubTileLayoutKind.Grid:
		case HubTileLayoutKind.List:
		case HubTileLayoutKind.Spotlight:
			return true;
		case HubTileLayoutKind.Feed:
			return false;
		default:
			never(layout);
	}
}
